import { useCallback, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { DateTime } from 'luxon'
import { observer } from 'mobx-react'

import { Card, CardHeader, PrimaryButton } from 'Components/_theme'

import { isActiveLanguage } from 'Utilities'

import { CountriesDE } from 'Constants/CountriesDE'
import { CountriesEN } from 'Constants/CountriesEN'
import { SupportedLanguagesEnum } from 'Constants/locale'

import { ShippingAddressCardProps } from './ShippingAddressCard.types'

export const ShippingAddressCard = observer(
  (props: ShippingAddressCardProps) => {
    const {
      name,
      nameAddition,
      street,
      streetAddition,
      zipcode,
      city,
      country,
      employee,
      shipping,
      showTracking,
      trackingURL,
    } = props

    const intl = useIntl()

    const isGermanLanguageActive = isActiveLanguage(SupportedLanguagesEnum.DE)

    const handleTrackOrder = useCallback(() => {
      if (!trackingURL) {
        return
      }

      window.open(trackingURL, '_blank')
    }, [trackingURL])

    const renderLine = (value: string | null | undefined) => {
      if (!value) {
        return null
      }

      return <p className="m-0">{value}</p>
    }

    const shippedOverTenDaysAgo = useMemo(() => {
      const shippingDate = DateTime.fromJSDate(new Date(shipping?.performedAt))
      const daysFromShippingDate = shippingDate.diffNow('days')

      if (Math.abs(daysFromShippingDate.days) >= 10) {
        return true
      }

      return false
    }, [shipping?.performedAt])

    return (
      <Card className="ShippingAddressCard mb-6">
        <CardHeader>
          <FormattedMessage id="ShippingAddressCard_shipping" />
        </CardHeader>

        <div className="ShippingAddressCard-body">
          {showTracking && !shippedOverTenDaysAgo && (
            <>
              <div className="d-flex flex-column flex-md-row align-items-md-center">
                <div className="flex-grow-1">
                  <span className="ShippingAddressCard-title fw-normal">
                    <FormattedMessage id="ShippingAddressCard_delivery_status" />
                  </span>

                  <p className="m-0 pe-2">
                    <FormattedMessage id="ShippingAddressCard_package_on_way" />
                  </p>
                </div>

                <PrimaryButton
                  className="text-nowrap mt-4 mt-md-0"
                  onClick={handleTrackOrder}
                >
                  <FormattedMessage id="ShippingAddressCard_track_order" />
                </PrimaryButton>
              </div>

              <hr className="my-4" />
            </>
          )}

          <span className="ShippingAddressCard-title mt-0">
            <FormattedMessage id="ShippingAddressCard_shipping_address" />
          </span>

          {renderLine(name)}

          {name !== nameAddition && renderLine(nameAddition)}

          {renderLine(street)}

          {renderLine(streetAddition)}

          {renderLine(`${zipcode} ${city}`)}

          {renderLine(
            isGermanLanguageActive
              ? CountriesDE[country]
              : CountriesEN[country],
          )}

          {employee &&
            employee !== name &&
            employee !== nameAddition &&
            renderLine(
              `${intl.formatMessage({
                id: 'Employee',
              })}: ${employee}`,
            )}
        </div>
      </Card>
    )
  },
)
